/**
 * Needs you: everything on the case that needs a person, worked top to
 * bottom — a rail of counts by section on top, the list beside the item
 * it's on, which opens at its top: what the case agent asks and why, then
 * what it's decided on, with its answers. An answer moves to the next item
 * down; an item an agent settles meanwhile leaves the list, and the person
 * is told once; a new one joins its section. `j` and `k` move between
 * items; each pane has its own keys. A review issue is worked in the
 * draft's margin, so its row opens the Document at it.
 *
 * The item in focus lives in the URL (`intervention`, or `issue`), so the
 * place survives a trip to the Document and back; what it remembers
 * (`NeedsYouMemory`) lives in the workbench, so what an agent settled
 * meanwhile is said on the way back.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { Stack, Text } from '@mantine/core';

import {
  isRework,
  sectionCount,
  SECTIONS,
  type ChecklistItem,
  type Intervention,
  type ReviewIssue,
  type Section,
  type WorkbenchDocument,
  type WorkbenchHeader,
} from '@/types/workbench';
import { NeedsYouList } from './NeedsYouList';
import {
  askChecklistItem,
  liveDraftKey,
  needsYouItems,
  needsYouSections,
  nextAfter,
  settledWhileWorking,
  workOrder,
  type NeedsYouItem,
  type NeedsYouMemory,
} from './needsYouOrder';
import { NeedsYouRail } from './NeedsYouRail';
import { ApprovalPane } from './panes/ApprovalPane';
import { IdentityPane } from './panes/IdentityPane';
import { IssuePointer } from './panes/IssuePointer';
import { QuestionPane } from './panes/QuestionPane';
import { ReworkPane } from './panes/ReworkPane';
import { SearchPane } from './panes/SearchPane';
import { useWorkbenchKeys } from './useWorkbenchKeys';
import classes from './NeedsYou.module.css';

interface NeedsYouProps {
  header: WorkbenchHeader;
  /** The case's asks (the open ones are listed). */
  asks: Intervention[];
  issues: ReviewIssue[] | null;
  documents: WorkbenchDocument[] | null;
  checklist: ChecklistItem[] | null;
  /** Everything Needs you reads has come back. */
  loaded: boolean;
  /** The item in focus (an ask's id, or a review issue's). */
  selectedId: string | null;
  /** What the person closed and the list as it last stood, kept across
   * views. */
  memory: NeedsYouMemory;
  onSelect: (item: NeedsYouItem | null) => void;
  /** A record is open beside the view: the list narrows. */
  recordOpen: boolean;
  /** Open a review issue in the draft. */
  onOpenIssue: (issue: ReviewIssue) => void;
  /** After an answer: the case refetches. */
  onAnswered: () => void | Promise<void>;
  /** Open Setup, where partner work is approved. */
  onOpenSetup?: () => void;
  /** Open a document (a partner's return a rework is about). */
  onOpenDocument?: (documentId: string) => void;
}

/** "2 identity questions and 1 manual search settled while you worked." */
function settledText(settled: Partial<Record<Section, number>>): string | null {
  const parts = SECTIONS.flatMap((s) => (settled[s] ? [sectionCount(s, settled[s] ?? 0)] : []));
  if (parts.length === 0) {
    return null;
  }
  const list =
    parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`;
  return `${list} settled while you worked.`;
}

export function NeedsYou({
  header,
  asks,
  issues,
  documents,
  checklist,
  loaded,
  selectedId,
  memory,
  onSelect,
  recordOpen,
  onOpenIssue,
  onAnswered,
  onOpenSetup,
  onOpenDocument,
}: Readonly<NeedsYouProps>) {
  const draftKey = liveDraftKey(header.needsYou.draftId, documents);
  const sections = useMemo(
    () =>
      needsYouSections(
        needsYouItems(asks, issues, draftKey, checklist),
        header.subjects.map((s) => s.id)
      ),
    [asks, issues, draftKey, checklist, header.subjects]
  );
  const order = useMemo(() => workOrder(sections), [sections]);
  const items = useMemo(() => new Map((checklist ?? []).map((c) => [c.id, c])), [checklist]);
  const names = useMemo(
    () => new Map(header.subjects.map((s) => [s.id, s.name])),
    [header.subjects]
  );

  // The items opened this session: "All not them" waits for each.
  const [opened, setOpened] = useState<Set<string>>(() => new Set());
  const [notice, setNotice] = useState<string | null>(null);

  const selected = order.find((item) => item.id === selectedId) ?? null;

  const select = useCallback(
    (item: NeedsYouItem | null) => {
      onSelect(item);
      if (item) {
        setOpened((prev) => (prev.has(item.id) ? prev : new Set([...prev, item.id])));
      }
    },
    [onSelect]
  );

  // Top to bottom: when the item in focus leaves, the one below it is next;
  // with nothing in focus, the first. What left without the person's answer
  // is said once.
  useEffect(() => {
    if (!loaded) {
      return;
    }
    const before = memory.previous;
    if (before.length > 0) {
      const text = settledText(settledWhileWorking(before, order, memory.answered));
      if (text) {
        setNotice(text);
      }
    }
    if (selectedId === null || !order.some((item) => item.id === selectedId)) {
      const next = selectedId === null ? (order[0] ?? null) : nextAfter(before, order, selectedId);
      if (next?.id !== selectedId) {
        select(next);
      }
    } else if (!opened.has(selectedId)) {
      setOpened((prev) => new Set([...prev, selectedId]));
    }
    memory.previous = order;
  }, [order, loaded, selectedId]);

  const step = (delta: 1 | -1) => {
    if (order.length === 0) {
      return;
    }
    const at = order.findIndex((item) => item.id === selectedId);
    const to =
      at === -1 ? (delta === 1 ? 0 : order.length - 1) : (at + delta + order.length) % order.length;
    select(order[to]);
  };
  useWorkbenchKeys({ j: () => step(1), k: () => step(-1) });

  const answeredBy = useCallback(
    async (ids: string[]) => {
      for (const id of ids) {
        memory.answered.add(id);
      }
      await onAnswered();
    },
    [memory, onAnswered]
  );

  const onSection = (section: Section) => {
    const first = sections.find((s) => s.section === section)?.groups[0]?.items[0];
    if (first) {
      select(first);
    }
  };

  if (!loaded && order.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        Reading what needs you…
      </Text>
    );
  }

  const pane = () => {
    if (!selected) {
      return null;
    }
    if (selected.kind === 'issue') {
      return (
        <IssuePointer
          key={selected.id}
          issue={selected.issue}
          holdsRelease={header.needsYou.releaseWaitsOn?.includes(selected.id) ?? false}
          onOpen={() => onOpenIssue(selected.issue)}
          keyboard
        />
      );
    }
    const { ask } = selected;
    const subjectName = selected.subjectId ? (names.get(selected.subjectId) ?? null) : null;
    const item = items.get(askChecklistItem(ask) ?? '') ?? null;
    if (isRework(ask)) {
      return (
        <ReworkPane
          key={ask.id}
          reportId={header.reportId}
          ask={ask}
          subjectName={subjectName}
          onAnswered={answeredBy}
          onOpenDocument={onOpenDocument}
          keyboard
        />
      );
    }
    if (ask.kind === 'approval') {
      return (
        <ApprovalPane
          key={ask.id}
          header={header}
          ask={ask}
          subjectName={subjectName}
          onAnswered={answeredBy}
          onOpenSetup={onOpenSetup}
          keyboard
        />
      );
    }
    switch (ask.form) {
      case 'identity':
        return (
          <IdentityPane
            key={ask.id}
            header={header}
            ask={ask}
            subjectName={subjectName}
            others={order.flatMap((other) =>
              other.kind === 'ask' &&
              other.ask.form === 'identity' &&
              other.subjectId === selected.subjectId &&
              other.id !== ask.id
                ? [other.ask]
                : []
            )}
            opened={opened}
            onAnswered={answeredBy}
            keyboard
          />
        );
      case 'search':
        return (
          <SearchPane
            key={ask.id}
            reportId={header.reportId}
            ask={ask}
            subjectName={subjectName}
            item={item}
            onAnswered={answeredBy}
            keyboard
          />
        );
      default:
        return (
          <QuestionPane
            key={ask.id}
            reportId={header.reportId}
            ask={ask}
            subjectName={subjectName}
            item={item}
            onAnswered={answeredBy}
            keyboard
          />
        );
    }
  };

  return (
    <Stack gap="sm" data-testid="needs-you">
      <NeedsYouRail
        sections={sections}
        releaseWaitsOn={header.needsYou.releaseWaitsOn}
        onSection={onSection}
      />
      {order.length === 0 ? (
        <Text size="sm" c="teal.8" data-testid="needs-you-clear">
          Nothing needs you.
          {header.needsYou.releaseWaitsOn?.length === 0 ? ' The draft can be released.' : ''}
        </Text>
      ) : (
        <div className={classes.panes}>
          <div className={classes.list} data-narrow={recordOpen}>
            <NeedsYouList
              header={header}
              sections={sections}
              selectedId={selected?.id ?? null}
              onSelect={select}
              notice={notice}
              onNoticeClose={() => setNotice(null)}
            />
          </div>
          <div className={classes.detail} data-testid="needs-you-detail">
            {pane()}
          </div>
        </div>
      )}
    </Stack>
  );
}
